<template>
  <div class="full-width d-flex flex-column align-start">
    <p class="ml-2 mt-3" v-if="isModal">
      To view your full transaction activity, go to the
      <a @click="loadWallet">Wallet page</a>.
    </p>
    <v-data-table
      :headers="columns"
      :hide-default-header="isModal && !isMobile"
      :hide-default-footer="isModal || (!isLoading && transactions.length < 10)"
      :items="transactions"
      item-key="key"
      :items-per-page="isModal ? 5 : 10"
      class="full-width"
      sort-by="description"
      :multi-sort="false"
      :custom-sort="customSort"
      :sort-desc="true"
      must-sort
      :class="[
        !isModal ? 'txn-table' : '',
        isModal && isMobile ? 'txn-table-mobile' : '',
        `elevation-${elevation}`,
        elevation == 0 ? 'light-grey-border' : '',
      ]"
      :search="searchValue"
      :loading="isLoading"
    >
      <template v-slot:[`header.description`]="{ header }">
        <Typography variant="subtitle-2" as="span">
          {{ header.text }}
        </Typography>
      </template>
      <template v-slot:[`header.recatagorizeItem`]="{ header }">
        <Typography variant="subtitle-2" as="span" class="font-weight-bold">
          {{ header.text }}
        </Typography>
      </template>
      <template v-slot:[`header.amount`]="{ header }">
        <Typography variant="subtitle-2" as="span" class="font-weight-bold1">
          {{ header.text }}
        </Typography>
      </template>
      <template v-slot:no-data>
        <Typography variant="body-2" class="black--text">No transactions found</Typography>
      </template>
      <template v-slot:no-results>
        <Typography variant="body-2" class="black--text">No transactions found</Typography>
      </template>
      <template v-slot:loading>
        <Typography variant="body-2" class="black--text">Loading... Please wait</Typography>
      </template>

      <template v-slot:[`item.amount`]="{ item }">
        <Typography
          variant="body-2"
          as="span"
          :class="{
            'success--text': !!item.isAccountCredit,
          }"
        >
          {{ item.amount }}
        </Typography>
      </template>
      <template v-slot:[`item.description`]="{ item }">
        <div class="d-flex">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-if="item.isPending"
                v-on="on"
                v-bind="attrs"
                large
                class="mb-1 mr-2"
                style="min-width: 16px; max-width: 16px"
                :class="item.isPending ? '' : 'hidden'"
                color="orange"
                >mdi-circle-small</v-icon
              >
            </template>
            Pending Transaction
          </v-tooltip>
          <div class="d-flex flex-column word-break py-4">
            <Typography class="mb-1 transaction-description" variant="body-2" as="span">
              {{ item.description }}
            </Typography>
            <Typography variant="body-2" as="span">
              {{ item.formattedDate }}
            </Typography>
          </div>
        </div>
      </template>
      <template v-slot:[`item.recatagorizeItem`]="{ item }">
        <v-select
          v-if="
            item.canRecategorize == true &&
            item.recategorizationOptions &&
            item.recategorizationOptions.length > 0
          "
          @change="recatagorizeTransactionHandler(item)"
          :items="item.recategorizationOptions"
          item-text="label"
          item-value="id"
          outlined
          dense
          hide-details
          class="budget-select word-break"
          style="max-width: 250px"
          v-model="item.recategorizationDropdownValue"
        ></v-select>
        <div
          v-else-if="item.originalBudget && item.originalBudget != null"
          class="d-flex flex-column"
        >
          <Typography
            variant="body-2"
            as="span"
            v-for="budget in item.originalBudget"
            :key="budget"
            >{{ budget }}</Typography
          >
        </div>
      </template>
      <template v-slot:[`item.disputed`]="{ item }">
        <div v-if="item.canDispute">
          <v-btn icon :loading="true" v-if="loading.disputeId == item.id"></v-btn>
          <v-menu max-width="260" offset-y v-else-if="item.canDispute && !item.isDisputed">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon text v-on="on" v-bind="attrs"><v-icon>mdi-dots-horizontal</v-icon></v-btn>
            </template>
            <v-list>
              <v-list-item @click="prepareDisputeDialog(item.id)">
                <v-list-item-title class="text-left"
                  ><v-icon color="black" class="mr-2">mdi-flag</v-icon>Dispute
                  transaction</v-list-item-title
                >
              </v-list-item>
            </v-list>
          </v-menu>
          <v-tooltip top open-delay="300" v-else-if="item.isDisputed">
            <template v-slot:activator="{ on, attrs }">
              <div v-on="on" v-bind="attrs" class="dispute-button">
                <v-icon color="error">mdi-alert</v-icon>
              </div>
            </template>
            <span>This transaction has been disputed</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
    <v-dialog v-model="display.dispute" persistent width="500">
      <v-card rounded="0" class="d-flex justify-center flex-column pa-6">
        <div class="d-flex justify-center align-center mx-2 mb-5">
          <v-icon v-if="display.disputeSuccess" color="success" x-large class="exit-warning-icon"
            >mdi-check</v-icon
          >
          <v-icon v-else color="error" x-large class="exit-warning-icon">mdi-alert</v-icon>
          <v-card-title class="word-break align-text-left exit-warning-text">
            {{ display.disputeSuccess ? "All set!" : "Oh no!" }}
          </v-card-title>
        </div>

        <v-textarea
          v-if="!display.disputeSuccess"
          label="Tell us what happened"
          outlined
          dense
          color="brandCyan"
          height="100"
          v-model="dispute.reason"
        ></v-textarea>
        <p v-if="!display.disputeSuccess">
          Reminder: Disputing this will lock your card to prevent any further suspicious activity so
          that our team can investigate on your behalf.
        </p>
        <p v-else>Our team will be in contact with you within 1 business day.</p>
        <CustomerServiceContact content="email" />
        <p class="error--text" v-if="dispute.error">{{ dispute.error }}</p>
        <v-card-actions class="mx-12 mt-5" v-if="display.disputeSuccess">
          <v-btn
            class="mx-auto white--text"
            color="brandCyan"
            depressed
            @click="display.dispute = false"
            width="130"
            >Close</v-btn
          >
        </v-card-actions>
        <v-card-actions class="mx-12 mt-5" v-else>
          <v-btn
            class="mx-auto white--text"
            color="brandCyan"
            text
            :disabled="loading.dispute"
            @click="display.dispute = false"
            width="130"
            >Cancel</v-btn
          >
          <v-btn
            class="mx-auto white--text"
            color="brandCyan"
            depressed
            @click="disputeTransaction"
            width="130"
            :disabled="!dispute.reason"
            :loading="loading.dispute"
            >Submit</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-if="recatagorizeTransaction.transaction != null"
      v-model="recatagorizeTransaction.showModal"
      width="500"
      persistent
    >
      <v-card rounded="0" class="d-flex justify-center flex-column pa-6">
        <div class="d-flex justify-space-between align-center mx-2">
          <v-card-title class="word-break align-text-left"> Recategorize Transaction </v-card-title>
        </div>
        <!-- Merchant name - recategorizationAmountToBeMoved -->
        <v-card-subtitle class="word-break pb-1 align-text-left"
          >{{ recatagorizeTransaction.transaction.description }}
          {{ recatagorizeTransaction.transaction.amount }}</v-card-subtitle
        >
        <transition name="topic-left" mode="out-in">
          <p
            class="py-2 mx-2"
            key="0"
            v-if="
              recatagorizeTransaction.success != true &&
              recatagorizeTransaction.error != true &&
              recatagorizeTransaction.loading != true
            "
          >
            {{
              formatCurrency(recatagorizeTransaction.transaction.recategorizationAmountToBeMoved)
            }}
            will be deducted from your general balance and transferred to
            {{ formatList(recatagorizeTransaction.transaction.originalBudget || []) }}.
          </p>
          <!-- Loading Robin -->
          <div
            v-else-if="recatagorizeTransaction.loading == true"
            class="mx-2 pb-2 d-flex flex-column align-center"
            key="1"
          >
            <Robin animation="flying" :width="150" class="mx-auto robin-animation" :loop="true" />
            Transferring funds...
          </div>
          <!-- Success message -->
          <div
            v-else-if="recatagorizeTransaction.success == true"
            class="mx-2 pb-2 d-flex flex-column align-center"
            key="2"
          >
            <Robin animation="ohYeah" :width="150" class="mx-auto robin-animation" :loop="true" />
            Awesome! The funds were successfully transferred!
          </div>
          <!-- Error message -->
          <div
            v-else-if="
              recatagorizeTransaction.error == true &&
              recatagorizeTransaction.insufficientFunds != true
            "
            class="mx-2 pb-2 pt-2"
            key="3"
          >
            <p class="pl-4 full-width align-text-left">
              It looks like there was an error transferring funds.
            </p>
            <CustomerServiceContact layout="stacked" class="pl-4 full-width align-text-left" />
          </div>
          <!-- Insufficient funds message -->
          <div
            v-else-if="
              recatagorizeTransaction.error == true &&
              recatagorizeTransaction.insufficientFunds == true
            "
            class="mx-2 pb-2 pt-2"
            key="4"
          >
            <p class="pl-4 align-text-left full-width">
              It looks like there aren't enough funds in your general balance to do the transaction.
            </p>
          </div>
        </transition>
        <!-- Buttons -->
        <div :class="{ hidden: recatagorizeTransaction.loading == true }">
          <v-card-actions
            class="mx-8"
            v-if="recatagorizeTransaction.error != true && recatagorizeTransaction.success != true"
          >
            <v-btn
              color="brandCyan"
              @click="closeRecatagorizationTransaction"
              outlined
              width="170"
              class="mr-4"
              >Cancel</v-btn
            >
            <v-spacer />
            <v-btn
              color="brandCyan"
              depressed
              class="white--text"
              @click="submitRecatagorizeTransaction"
              width="170"
              >Submit</v-btn
            >
          </v-card-actions>
          <v-card-actions class="mx-8" v-else>
            <v-btn
              color="brandCyan"
              depressed
              @click="resetRecatagorizeTransactionState"
              width="170"
              class="mx-auto white--text"
              >Done</v-btn
            >
          </v-card-actions>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Services
import MarqetaService from "@/services/MarqetaService";
import WalletService from "@/services/WalletService";
import WalletServiceV3 from "@/services/WalletServiceV3";

// Components
import Robin from "@/components/Robin.vue";
import Typography from "@/components/generic/Typography.vue";
import CustomerServiceContact from "@/components/help/CustomerServiceContact";

// Mixins
import formatCurrencyMixin from "@/mixins/formatCurrency";
import formatListMixin from "@/mixins/formatList";

// Utils
import moment from "moment";
import { mapState } from "vuex";

export default {
  name: "TransactionLog",
  props: {
    isModal: {
      type: Boolean,
      default: false,
    },
    // Filter payments for a certain user
    byUser: String,
    clientId: Number,
    elevation: {
      type: Number,
      default: 2,
    },
    onlyEssentialColumns: {
      type: Boolean,
      default: false,
    },
    hideCredits: {
      type: Boolean,
      default: false,
    },
    search: String,
  },
  components: { Robin, Typography, CustomerServiceContact },
  mixins: [formatCurrencyMixin, formatListMixin],
  data() {
    return {
      walletServiceV3: new WalletServiceV3(),
      loading: {
        balance: false,
        disputeId: null,
        dispute: false,
        user: false,
      },
      data: {
        // When filtering for a specific user that isn't the caller
        // Used for the people page
        transactions: [],
      },
      display: {
        virtualCard: false,
        policies: false,
        management: false,
        transactions: false,
        authorizationSearch: false,
        transactionSearch: false,
        dispute: false,
        disputeSuccess: false,
      },
      dispute: {
        token: null,
        reason: null,
        error: null,
      },
      transactionColumns: [
        {
          text: "Description  ",
          value: "description",
          width: "50%",
          sortable: true,
          align: "start",
          modal: true,
        },
        {
          text: "Budget Category",
          value: "recatagorizeItem",
          width: "20%",
          align: "start",
          sortable: false,
          modal: false,
        },
        {
          text: "Amount",
          value: "amount",
          width: "15%",
          align: "end",
          sortable: true,
          modal: true,
        },
        {
          text: "",
          value: "disputed",
          width: "10%",
          align: "center",
          sortable: false,
          modal: false,
        },
      ],
      recatagorizeTransaction: {
        showModal: false,
        transaction: null,
        selectedBudget: null,
        success: false,
        error: false,
        insufficientFunds: false,
        loading: false,
      },
    };
  },
  created() {},
  mounted() {
    if (this.byUser) this.getTransactions();
    var mostRecentTx = this.transactions.find((transaction) => transaction.type == "load");

    if (
      !this.isModal &&
      mostRecentTx &&
      this.userProfile.LoginHistories.length >= 3 &&
      mostRecentTx.date >= this.userProfile.LoginHistories[2].date &&
      mostRecentTx.date != this.confettiPlayed
    ) {
      this.$emit("trigger-confetti", mostRecentTx.date);
    }
  },
  beforeUnmount() {},
  methods: {
    loadWallet() {
      this.$emit("load-wallet");
    },
    customSort(items, index, isDesc) {
      // console.log({ isDesc });
      items.sort((a, b) => {
        if (index.length && index[0] === "description") {
          // console.log("Parsing date sort", b.date);
          if (isDesc.length && !isDesc[0]) {
            return moment(b.date).isBefore(a.date) ? 1 : -1;
          } else {
            return moment(a.date).isBefore(b.date) ? 1 : -1;
          }
        }
        if (index.length && index[0] === "amount") {
          // Always put items with amountWithSign === null at the bottom
          if (a.amountWithSign === null && b.amountWithSign !== null) {
            return 1; // a goes after b
          }
          if (a.amountWithSign !== null && b.amountWithSign === null) {
            return -1; // a goes before b
          }
          // If both are null or both are not null, sort normally
          if (isDesc.length && !isDesc[0]) {
            return a.amountWithSign - b.amountWithSign;
          } else {
            return b.amountWithSign - a.amountWithSign;
          }
        }
      });
      return items;
    },
    async getTransactions() {
      if (!this.byUser) return;
      console.log("loading transactions", this.byUser);
      try {
        this.loading.user = true;
        const res = await this.walletServiceV3.getWalletProfile(this.byUser, {
          clientId: this.clientId,
        });
        this.data.transactions = res?.result?.transactions || [];
      } catch (e) {
        console.log("Failed to get transactions for user ", e);
      } finally {
        this.loading.user = false;
      }
    },
    prepareDisputeDialog(token) {
      this.display.disputeSuccess = false;
      this.display.dispute = true;
      this.dispute.token = token;
      this.dispute.reason = null;
      this.dispute.error = null;
      this.loading.dispute = false;
    },
    async disputeTransaction() {
      try {
        this.dispute.error = null;
        this.loading.dispute = true;
        const token = this.dispute.token;
        console.log("Disputing transaction ", token);
        var dispute = {
          clientId: this.userProfile.clientId,
          userId: this.userProfile.userId,
          transactionToken: token,
          reason: this.dispute.reason,
          email: this.userProfile.businessEmail,
          phone: this.userProfile.businessPhone,
          name: this.userProfile.firstName + " " + this.userProfile.lastName,
          createdBy: this.userProfile.userId,
          updatedBy: this.userProfile.userId,
        };
        const disputeRes = await MarqetaService.createDispute(dispute, this.magicLinkToken);
        console.log("Dispute response: ", disputeRes);
        this.$store.dispatch("setWalletProfile", this.userProfile.userId);
        this.display.disputeSuccess = true;
      } catch (e) {
        console.log("Error sending dispute");
        this.dispute.error = "Failed to submit dispute";
      } finally {
        this.loading.dispute = false;
      }
    },
    recatagorizeTransactionHandler(transaction) {
      console.log("recatagorizing transaction", transaction);
      console.log(transaction.recategorizationDropdownValue);
      if (transaction.recategorizationDropdownValue != 0) {
        return;
      } else {
        this.recatagorizeTransaction.showModal = true;
        this.recatagorizeTransaction.transaction = transaction;
        const selectedBudget = transaction.recategorizationOptions.find((x) => x.id == 0);
        this.recatagorizeTransaction.selectedBudget = selectedBudget;
      }
    },
    submitRecatagorizeTransaction() {
      var startTime = new Date();
      this.$set(this.recatagorizeTransaction, "loading", true);
      const cardActivityToken = this.recatagorizeTransaction.transaction.id;
      WalletService.recatagorizeTransaction(cardActivityToken, this.magicLinkToken)
        .then((res) => {
          console.log("Response from recat ", res);

          var timeDiff = moment().diff(moment(startTime));
          if (timeDiff >= 1500) {
            timeDiff = 1500;
          }
          setTimeout(() => {
            this.$set(this.recatagorizeTransaction, "loading", false);
            if (res.error == undefined) {
              this.recatagorizeTransaction.success = true;
              // Set the activity to not display the dropdown
              // find activity index
              const index = this.walletProfile?.transactions?.findIndex((x) => {
                return x.id == this.recatagorizeTransaction.transaction.id;
              });
              // Set the dropdown value back to the one that isn't general
              this.$set(this.walletProfile.transactions[index], "canRecategorize", false);
              // this.refreshWallet();
              this.$store.dispatch("refreshWallet");
            } else {
              if (res.error_code === "1070100") {
                this.recatagorizeTransaction.error = true;
                this.recatagorizeTransaction.insufficientFunds = true;
              } else {
                this.recatagorizeTransaction.error = true;
              }
            }
          }, 1500 - timeDiff);
        })
        .catch((error) => {
          console.log(error);
          this.$set(this.recatagorizeTransaction, "loading", false);
          this.recatagorizeTransaction.error = true;
        });
    },
    resetRecatagorizeTransactionState() {
      this.recatagorizeTransaction = {
        showModal: false,
        transaction: null,
        selectedBudget: null,
        success: false,
        error: false,
        insufficientFunds: false,
      };
    },
    closeRecatagorizationTransaction() {
      // find activity index
      const index = this.walletProfile.transactions.findIndex((x) => {
        return x.id == this.recatagorizeTransaction.transaction.id;
      });
      // Find option that isn't general
      const value = this.walletProfile.transactions[index].recategorizationOptions.find(
        (tx) => tx.id != 0
      );
      // Set the dropdown value back to the one that isn't general
      this.$set(this.walletProfile.transactions[index], "recategorizationDropdownValue", value);
      this.resetRecatagorizeTransactionState();
    },
    // Passes in an array of objects to recatagorize a transaction, we want to find the one that isn't 0
    initialDropdownOptionSelectValue(options) {
      return options.find((x) => x.id != 0);
    },
  },
  computed: {
    ...mapState([
      "userProfile",
      "walletProfile",
      "balances",
      "cashBalance",
      "confettiPlayed",
      "magicLinkToken",
    ]),
    isMobile() {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
    isLoading() {
      return this.loading.user || (!this.isModal && this.walletProfile?.loading);
    },
    columns() {
      let col = this.transactionColumns;
      // if (this.isMobile) col[0].align = "end";
      // else col[0].align = "start";
      if (this.isModal || this.onlyEssentialColumns) return col.filter((x) => x.modal);
      // else if (this.isMobile) return col.filter(x => x.mobile !== false);
      return col;
    },
    transactions() {
      if (this.byUser && this.loading.user) return [];
      else if (!this.byUser && (!this.walletProfile || !this.walletProfile.transactions)) return [];
      var auth = this.byUser ? this.data.transactions : this.walletProfile.transactions;
      if (this.hideCredits) auth = auth.filter((x) => x.type !== "load");
      auth.forEach((a) => {
        a.formattedDate = moment(a.date).local().format("MMM D, YYYY");
        if (a.canRecategorize == true) {
          a.recategorizationDropdownValue = a.recategorizationOptions.find((tx) => {
            return tx.id != 0;
          });
        }
      });
      return auth;
    },
    searchValue() {
      // if (this.search)
      return this.search;
      // return this.search.transactions;
    },
  },
  watch: {},
};
</script>

<style scoped>
/* Removes header on mobile to sort columns */
.txn-table-mobile >>> .v-data-table__wrapper > table > thead {
  display: none;
}

/* Make footer page count text larger */
::v-deep .v-data-footer {
  font-size: 14px;
}

/* Truncate merchant name to 2 lines */
.transaction-description {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* Number of lines to show */
  overflow: hidden;
  text-overflow: ellipsis; /* Adds the ellipsis (...) */
}
</style>
